import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import { CheckCircle, AlertCircle, X } from 'lucide-react';
import { API_ENDPOINTS } from '../../../../utils/config';

const REQUEST_TIMEOUT_MS = 20000;

const PayrollModal = ({ isOpen, onClose, payroll, employees, onSave, mode = 'add' }) => {
  const [formData, setFormData] = useState({
    employee: '',
    pay_period_start: '',
    pay_period_end: '',
    payroll_data: {
      gross_salary: '',
      basic_salary: '',
      allowances: {
        house_rent_allowance: '',
        transport_allowance: '',
        medical_allowance: ''
      },
      deductions: {
        income_tax: '',
        provident_fund: '',
        professional_tax: ''
      },
      net_salary: '',
      status: 'pending',
      payment_method: 'bank_transfer',
      bank_details: {
        account_number: '',
        ifsc_code: ''
      }
    }
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (payroll && mode === 'edit') {
        setFormData({
          employee: payroll?.employee || '',
          pay_period_start: payroll?.pay_period_start || '',
          pay_period_end: payroll?.pay_period_end || '',
          payroll_data: {
            gross_salary: payroll?.payroll_data?.gross_salary || '',
            basic_salary: payroll?.payroll_data?.basic_salary || '',
            allowances: {
              house_rent_allowance: payroll?.payroll_data?.allowances?.house_rent_allowance || '',
              transport_allowance: payroll?.payroll_data?.allowances?.transport_allowance || '',
              medical_allowance: payroll?.payroll_data?.allowances?.medical_allowance || ''
            },
            deductions: {
              income_tax: payroll?.payroll_data?.deductions?.income_tax || '',
              provident_fund: payroll?.payroll_data?.deductions?.provident_fund || '',
              professional_tax: payroll?.payroll_data?.deductions?.professional_tax || ''
            },
            net_salary: payroll?.payroll_data?.net_salary || '',
            status: payroll?.payroll_data?.status || 'pending',
            payment_method: payroll?.payroll_data?.payment_method || 'bank_transfer',
            bank_details: {
              account_number: payroll?.payroll_data?.bank_details?.account_number || '',
              ifsc_code: payroll?.payroll_data?.bank_details?.ifsc_code || ''
            }
          }
        });
      } else {
        setFormData({
          employee: '',
          pay_period_start: '',
          pay_period_end: '',
          payroll_data: {
            gross_salary: '',
            basic_salary: '',
            allowances: {
              house_rent_allowance: '',
              transport_allowance: '',
              medical_allowance: ''
            },
            deductions: {
              income_tax: '',
              provident_fund: '',
              professional_tax: ''
            },
            net_salary: '',
            status: 'pending',
            payment_method: 'bank_transfer',
            bank_details: {
              account_number: '',
              ifsc_code: ''
            }
          }
        });
      }
      setErrors({});
      setSuccessMessage('');
      setErrorMessage('');
    }
  }, [isOpen, payroll, mode]);

  useEffect(() => {
    // Calculate net salary: Net = Gross - Deductions (Gross already includes Basic + Allowances)
    const gross = Number(formData.payroll_data.gross_salary) || 0;
    const deductions = 
      (Number(formData.payroll_data.deductions.income_tax) || 0) +
      (Number(formData.payroll_data.deductions.provident_fund) || 0) +
      (Number(formData.payroll_data.deductions.professional_tax) || 0);
    
    const netSalary = Math.round((gross - deductions) * 100) / 100;
    const currentNet = Number(formData.payroll_data.net_salary) || 0;
    if (Math.abs(netSalary - currentNet) > 0.01) {
      setFormData(prev => ({
        ...prev,
        payroll_data: {
          ...prev.payroll_data,
          net_salary: netSalary >= 0 ? netSalary.toString() : ''
        }
      }));
    }
  }, [
    formData.payroll_data.gross_salary,
    formData.payroll_data.deductions.income_tax,
    formData.payroll_data.deductions.provident_fund,
    formData.payroll_data.deductions.professional_tax
  ]);

  const handleInputChange = (path, value) => {
    setFormData(prev => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
    
    // Clear error for this field
    if (errors[path]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[path];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.employee) {
      newErrors['employee'] = 'Employee is required';
    }
    if (!formData.pay_period_start) {
      newErrors['pay_period_start'] = 'Pay period start date is required';
    }
    if (!formData.pay_period_end) {
      newErrors['pay_period_end'] = 'Pay period end date is required';
    }
    if (!formData.payroll_data.gross_salary) {
      newErrors['payroll_data.gross_salary'] = 'Gross salary is required';
    }
    if (!formData.payroll_data.basic_salary) {
      newErrors['payroll_data.basic_salary'] = 'Basic salary is required';
    }
    if (!formData.payroll_data.net_salary) {
      newErrors['payroll_data.net_salary'] = 'Net salary is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setErrorMessage('Please fill in all required fields');
      return;
    }
    
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const payload = {
        employee: Number(formData.employee),
        pay_period_start: formData.pay_period_start,
        pay_period_end: formData.pay_period_end,
        payroll_data: {
          gross_salary: Number(formData.payroll_data.gross_salary),
          basic_salary: Number(formData.payroll_data.basic_salary),
          allowances: {
            house_rent_allowance: Number(formData.payroll_data.allowances.house_rent_allowance) || 0,
            transport_allowance: Number(formData.payroll_data.allowances.transport_allowance) || 0,
            medical_allowance: Number(formData.payroll_data.allowances.medical_allowance) || 0
          },
          deductions: {
            income_tax: Number(formData.payroll_data.deductions.income_tax) || 0,
            provident_fund: Number(formData.payroll_data.deductions.provident_fund) || 0,
            professional_tax: Number(formData.payroll_data.deductions.professional_tax) || 0
          },
          net_salary: Number(formData.payroll_data.net_salary),
          status: formData.payroll_data.status,
          payment_method: formData.payroll_data.payment_method,
          bank_details: {
            account_number: formData.payroll_data.bank_details.account_number || '',
            ifsc_code: formData.payroll_data.bank_details.ifsc_code || ''
          }
        }
      };

      let response;
      if (mode === 'edit' && payroll?.id) {
        response = await axios.put(
          API_ENDPOINTS.PAYROLL_UPDATE(payroll.id),
          payload,
          { headers, timeout: REQUEST_TIMEOUT_MS }
        );
      } else {
        response = await axios.post(
          API_ENDPOINTS.PAYROLL_CREATE,
          payload,
          { headers, timeout: REQUEST_TIMEOUT_MS }
        );
      }

      if (response.data) {
        setSuccessMessage(mode === 'edit' ? 'Payroll updated successfully' : 'Payroll created successfully');
        setTimeout(() => {
          onSave();
          onClose();
        }, 1000);
      }
    } catch (error) {
      const apiError = error?.response?.data?.message || error?.response?.data?.detail || error?.message || 'Failed to save payroll';
      setErrorMessage(apiError);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg border border-gray-200 w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">
            {mode === 'edit' ? 'Edit Payroll' : 'Create Payroll'}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <X size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 p-6 space-y-6">
            {successMessage && (
              <div className="p-3 rounded-md border border-green-300 bg-green-50 text-green-700">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <p className="text-sm font-medium">{successMessage}</p>
                </div>
              </div>
            )}
            {errorMessage && (
              <div className="p-3 rounded-md border border-red-300 bg-red-50 text-red-700">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  <p className="text-sm">{errorMessage}</p>
                </div>
              </div>
            )}

            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Employee <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.employee}
                    onChange={(e) => handleInputChange('employee', e.target.value)}
                    className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ${errors.employee ? 'border-red-500' : ''}`}
                    required
                  >
                    <option value="">Select Employee</option>
                    {employees.map((emp) => {
                      const empData = emp?.employee_data || {};
                      const empName = empData?.employee_name || empData?.basic_information?.full_name || empData?.basic_information?.employee_name || `Employee ${emp.id}`;
                      return (
                        <option key={emp.id} value={emp.id}>
                          {empName} ({empData?.employee_id || empData?.basic_information?.employee_id || emp.id})
                        </option>
                      );
                    })}
                  </select>
                  {errors.employee && (
                    <p className="mt-1 text-sm text-red-500">{errors.employee}</p>
                  )}
                </div>
                
                <Input
                  label="Pay Period Start"
                  type="date"
                  value={formData.pay_period_start}
                  onChange={(e) => handleInputChange('pay_period_start', e.target.value)}
                  error={errors.pay_period_start}
                  required
                />
                
                <Input
                  label="Pay Period End"
                  type="date"
                  value={formData.pay_period_end}
                  onChange={(e) => handleInputChange('pay_period_end', e.target.value)}
                  error={errors.pay_period_end}
                  required
                />
              </div>
            </div>

            {/* Salary Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Salary Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Gross Salary"
                  type="number"
                  placeholder="75000"
                  value={formData.payroll_data.gross_salary}
                  onChange={(e) => handleInputChange('payroll_data.gross_salary', e.target.value)}
                  error={errors['payroll_data.gross_salary']}
                  required
                />
                
                <Input
                  label="Basic Salary"
                  type="number"
                  placeholder="60000"
                  value={formData.payroll_data.basic_salary}
                  onChange={(e) => handleInputChange('payroll_data.basic_salary', e.target.value)}
                  error={errors['payroll_data.basic_salary']}
                  required
                />
                
                <Input
                  label="Net Salary"
                  type="number"
                  placeholder="64000"
                  value={formData.payroll_data.net_salary}
                  onChange={(e) => handleInputChange('payroll_data.net_salary', e.target.value)}
                  error={errors['payroll_data.net_salary']}
                  required
                  disabled
                />
              </div>
            </div>

            {/* Allowances */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Allowances</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="House Rent Allowance"
                  type="number"
                  placeholder="12000"
                  value={formData.payroll_data.allowances.house_rent_allowance}
                  onChange={(e) => handleInputChange('payroll_data.allowances.house_rent_allowance', e.target.value)}
                />
                
                <Input
                  label="Transport Allowance"
                  type="number"
                  placeholder="2000"
                  value={formData.payroll_data.allowances.transport_allowance}
                  onChange={(e) => handleInputChange('payroll_data.allowances.transport_allowance', e.target.value)}
                />
                
                <Input
                  label="Medical Allowance"
                  type="number"
                  placeholder="1000"
                  value={formData.payroll_data.allowances.medical_allowance}
                  onChange={(e) => handleInputChange('payroll_data.allowances.medical_allowance', e.target.value)}
                />
              </div>
            </div>

            {/* Deductions */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Deductions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="Income Tax"
                  type="number"
                  placeholder="8000"
                  value={formData.payroll_data.deductions.income_tax}
                  onChange={(e) => handleInputChange('payroll_data.deductions.income_tax', e.target.value)}
                />
                
                <Input
                  label="Provident Fund"
                  type="number"
                  placeholder="3000"
                  value={formData.payroll_data.deductions.provident_fund}
                  onChange={(e) => handleInputChange('payroll_data.deductions.provident_fund', e.target.value)}
                />
                
                <Input
                  label="Professional Tax"
                  type="number"
                  placeholder="200"
                  value={formData.payroll_data.deductions.professional_tax}
                  onChange={(e) => handleInputChange('payroll_data.deductions.professional_tax', e.target.value)}
                />
              </div>
            </div>

            {/* Payment Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Payment Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Status
                  </label>
                  <select
                    value={formData.payroll_data.status}
                    onChange={(e) => handleInputChange('payroll_data.status', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="paid">Paid</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Payment Method
                  </label>
                  <select
                    value={formData.payroll_data.payment_method}
                    onChange={(e) => handleInputChange('payroll_data.payment_method', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
                
                <Input
                  label="Account Number"
                  type="text"
                  placeholder="1234567890123456"
                  value={formData.payroll_data.bank_details.account_number}
                  onChange={(e) => handleInputChange('payroll_data.bank_details.account_number', e.target.value)}
                />
                
                <Input
                  label="IFSC Code"
                  type="text"
                  placeholder="HDFC0001234"
                  value={formData.payroll_data.bank_details.ifsc_code}
                  onChange={(e) => handleInputChange('payroll_data.bank_details.ifsc_code', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : mode === 'edit' ? 'Update Payroll' : 'Create Payroll'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PayrollModal;

